import axios from 'axios';

// Configure your WordPress site URL here
const WORDPRESS_URL = process.env.REACT_APP_WORDPRESS_URL || 'https://yourfinservices.com.au';
const API_BASE_URL = `${WORDPRESS_URL}/wp-json`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increased to 2 minutes for better handling of idle periods
  withCredentials: true, // Include cookies in requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for file uploads with longer timeout
const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for file uploads (sufficient for 5MB files)
  withCredentials: true, // Include cookies in requests
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Add request interceptor for authentication
const addAuthHeaders = (config) => {
  // Prefer Basic Auth (Application Password) when available
  const basicAuth = localStorage.getItem('wp_auth');
  if (basicAuth) {
    config.headers.Authorization = `Basic ${basicAuth}`;
    return config;
  }

  // Fallback: JWT token if available (only when no Basic Auth)
  const token = localStorage.getItem('wp_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add nonce for WordPress security
  const nonce = localStorage.getItem('wp_nonce');
  if (nonce) {
    config.headers['X-WP-Nonce'] = nonce;
  }

  return config;
};


api.interceptors.request.use(addAuthHeaders, (error) => Promise.reject(error));
uploadApi.interceptors.request.use(addAuthHeaders, (error) => Promise.reject(error));

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('wp_token');
      localStorage.removeItem('wp_nonce');
      console.error('Authentication failed. Please log in again.');
    }
    return Promise.reject(error);
  }
);

/**
 * Upload media file to WordPress
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} - The uploaded media object
 */
export const uploadMedia = async (file, onProgress = null) => {
  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    throw new Error(`File size too large. Maximum size allowed is 5MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  }

  const formData = new FormData();
  formData.append('file', file, file.name);
  
  try {
    const response = await uploadApi.post('/wp/v2/media', formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
    
    return {
      id: response.data.id,
      url: response.data.source_url,
      alt: response.data.alt_text,
    };
  } catch (error) {
    console.error('Error uploading media:', error);
    if (error.code === 'ECONNABORTED') {
      throw new Error('Upload timeout. Please try with a smaller file or check your internet connection.');
    }
    throw new Error(`Failed to upload file: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Upload multiple media files to WordPress
 * @param {File[]} files - Array of files to upload
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Array>} - Array of uploaded media objects
 */
export const uploadMultipleMedia = async (files, onProgress = null) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    try {
      const fileProgress = (percent) => {
        if (onProgress) {
          const overallProgress = Math.round(((i + percent / 100) / totalFiles) * 100);
          onProgress(overallProgress, `Uploading ${file.name}...`);
        }
      };
      
      const result = await uploadMedia(file, fileProgress);
      results.push(result);
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      throw new Error(`Failed to upload ${file.name}: ${error.message}`);
    }
  }
  
  return results;
};

/**
 * Create a new post
 * @param {Object} postData - The post data
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} - The created post
 */
export const createPost = async (postData, onProgress = null) => {
  try {
    
    let featuredImageId = null;
    let galleryIds = [];

    // Process featured image if provided
    if (postData.featured_image) {
      if (onProgress) onProgress(10, 'Processing featured image...');
      const featuredImageData = await uploadMedia(postData.featured_image);
      featuredImageId = featuredImageData.id;
    }

    // Process gallery images if provided
    if (postData.gallery && postData.gallery.length > 0) {
      if (onProgress) onProgress(20, 'Processing gallery images...');
      const galleryData = await uploadMultipleMedia(postData.gallery, (progress, message) => {
        if (onProgress) onProgress(20 + (progress * 0.6), message); // 20-80% for gallery processing
      });
      galleryIds = galleryData.map(img => img.id);
    }

    // Prepare post data for API
    const apiPostData = {
      name_or_title: postData.name_or_title,
      context: postData.context,
      short_description: postData.short_description,
      featured_image: featuredImageId,
      gallery: galleryIds,
      gallery_style: postData.gallery_style || '1-column',
      content_category: postData.content_category,
      car_manufacturer: postData.car_manufacturer,
      car_brand: postData.car_brand,
      vehicle_location: postData.vehicle_location,
      engine_capacity: postData.engine_capacity,
      transmission: postData.transmission,
      drivetrain: postData.drivetrain,
      steering_wheel_position: postData.steering_wheel_position,
      mileage: postData.mileage,
      vehicle_type: postData.vehicle_type,
      condition: postData.condition,
      colour: postData.colour,
      interior_color: postData.interior_color,
      manufacture_year: postData.manufacture_year,
      engine: postData.engine,
      rego: postData.rego,
      sell_status: postData.sell_status,
      comment_status: postData.comment_status || 'closed',
    };

    // Create the post
    if (onProgress) onProgress(90, 'Creating post...');
    const response = await api.post('/post-publisher/v1/posts', apiPostData);
    
    if (onProgress) onProgress(100, 'Post created successfully!');
    return response.data;
  } catch (error) {
    console.error('Error creating post:', error);
    throw new Error(`Failed to create post: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Get posts with pagination
 * @param {number} page - Page number (default: 1)
 * @param {number} perPage - Posts per page (default: 10)
 * @returns {Promise<Object>} - Posts data with pagination info
 */
export const getPosts = async (page = 1, perPage = 10) => {
  try {
    const response = await api.get('/post-publisher/v1/posts', {
      params: {
        page,
        per_page: perPage,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw new Error(`Failed to fetch posts: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Get single post by ID
 * @param {number} postId - The post ID
 * @returns {Promise<Object>} - The post data
 */
export const getPost = async (postId) => {
  try {
    const response = await api.get(`/post-publisher/v1/posts/${postId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching post:', error);
    throw new Error(`Failed to fetch post: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Authenticate with WordPress
 * @param {string} username - WordPress username
 * @param {string} password - WordPress password or Application Password
 * @returns {Promise<Object>} - Authentication response
 */
export const authenticate = async (username, password) => {
  // List of websites to try authentication against
  const websites = [
    'https://yourfinservices.com.au',
    // Add more websites here as needed
  ];

  // Remove all spaces from Application Password (WordPress displays them with spaces for readability)
  const cleanPassword = (password || '').trim().replace(/\s+/g, '');
  const auth = btoa(`${username}:${cleanPassword}`);

  // Try to authenticate against each website
  for (const websiteUrl of websites) {
    try {
      // Try our custom endpoint
      const response = await fetch(`${websiteUrl}/wp-json/post-publisher/v1/session`, {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.is_logged_in) {
          // Store authentication data
          localStorage.setItem('wp_auth', auth);
          localStorage.setItem('wp_username', username);
          localStorage.setItem('wp_user_id', data.user.id);
          localStorage.setItem('current_website_url', websiteUrl);
          
          // Update axios instances with the newly authenticated website URL
          api.defaults.baseURL = `${websiteUrl}/wp-json`;
          uploadApi.defaults.baseURL = `${websiteUrl}/wp-json`;
          
          return { 
            success: true, 
            user: data.user,
            website: {
              url: websiteUrl,
              name: websiteUrl.replace('https://', '').replace('http://', '')
            }
          };
        }
      }
    } catch (error) {
      // Continue to next website if this one fails
      console.log(`Authentication failed for ${websiteUrl}:`, error.message);
      continue;
    }
  }

  // If all websites failed
  throw new Error('Invalid username or application password. User not found on any registered website.');
};

/**
 * Check if user is logged into WordPress using direct API call
 * @returns {Promise<Object>} - WordPress session status and user data
 */
export const checkWordPressSession = async () => {
  try {
    
    // Create a fresh axios instance for this request
    const sessionApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // Increased to 30 seconds
      withCredentials: true, // Include cookies in requests
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    // Try to check WordPress session using our custom endpoint
    const response = await sessionApi.get('/post-publisher/v1/session');
    
    if (response.data && response.data.is_logged_in) {
      // Store the session info for future use
      localStorage.setItem('wp_session_user', JSON.stringify(response.data.user));
      // Also store username for header display
      if (response.data.user.username) {
        localStorage.setItem('wp_username', response.data.user.username);
      }
      return { 
        isAuthenticated: true, 
        user: response.data.user 
      };
    } else {
      return { isAuthenticated: false };
    }
  } catch (error) {
    // Handle network timeout specifically
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('Session check timeout - this may be due to server being slow or network issues');
    }
    return { isAuthenticated: false };
  }
};

/**
 * Check WordPress session using the bridge method (fallback)
 * @returns {Promise<Object>} - WordPress session status and user data
 */
export const checkWordPressSessionBridge = async () => {
  return new Promise((resolve) => {
    
    // Create a popup window to check WordPress session
    const popup = window.open(
      `${WORDPRESS_URL}/wordpress-session-bridge.html`,
      'wp-session-check',
      'width=1,height=1,scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no'
    );
    
    if (!popup) {
      resolve({ isAuthenticated: false });
      return;
    }
    
    // Listen for the result from the popup
    const handleMessage = (event) => {
      if (event.origin !== WORDPRESS_URL) return;
      
      if (event.data.type === 'WORDPRESS_SESSION_RESULT') {
        // Clean up
        popup.close();
        window.removeEventListener('message', handleMessage);
        
        if (event.data.data.is_logged_in) {
          // Store username for header display
          if (event.data.data.user.username) {
            localStorage.setItem('wp_username', event.data.data.user.username);
          }
          resolve({ 
            isAuthenticated: true, 
            user: event.data.data.user 
          });
        } else {
          resolve({ isAuthenticated: false });
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Timeout after 3 seconds
    setTimeout(() => {
      popup.close();
      window.removeEventListener('message', handleMessage);
      resolve({ isAuthenticated: false });
    }, 3000);
  });
};

/**
 * Verify if stored authentication is still valid
 * @returns {Promise<Object>} - Authentication status and user data
 */
export const verifyAuth = async () => {
  try {
    const auth = localStorage.getItem('wp_auth');
    if (!auth) {
      return { isAuthenticated: false };
    }

    const verifyApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // Increased to 30 seconds
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${auth}`,
      },
    });

    const response = await verifyApi.get('/wp/v2/users/me');
    return { isAuthenticated: true, user: response.data };
  } catch (error) {
    console.error('Auth verification failed:', error);
    localStorage.removeItem('wp_auth');
    localStorage.removeItem('wp_username');
    return { isAuthenticated: false };
  }
};

/**
 * Get WordPress categories
 * @returns {Promise<Array>} - Array of category objects
 */
export const getCategories = async () => {
  try {
    const response = await api.get('/wp/v2/categories');
    return response.data.map(category => ({
      id: category.id,
      name: category.name,
      slug: category.slug,
      count: category.count
    }));
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw new Error(`Failed to fetch categories: ${error.response?.data?.message || error.message}`);
  }
};

/**
 * Get WordPress nonce for security
 * @returns {Promise<string>} - The nonce
 */
export const getNonce = async () => {
  try {
    const response = await api.get('/wp/v2/users/me');
    // Extract nonce from response headers or make a separate request
    // This is a simplified version - you might need to adjust based on your setup
    return response.headers['x-wp-nonce'] || '';
  } catch (error) {
    console.error('Error getting nonce:', error);
    return '';
  }
};



// Update post
export const updatePost = async (postId, postData, existingImages = {}, onProgress = null) => {
  try {
    let featuredImageId = null;
    let galleryIds = [];

    // Handle featured image
    if (postData.featured_image) {
      if (onProgress) onProgress(10, 'Uploading featured image...');
      const featuredImage = await uploadMedia(postData.featured_image);
      featuredImageId = featuredImage.id;
    } else if (existingImages.featured_image) {
      featuredImageId = existingImages.featured_image.id;
    }

    // Handle gallery images - only process new File objects
    if (postData.gallery && postData.gallery.length > 0) {
      // Filter only File objects (new uploads)
      const fileObjects = postData.gallery.filter(item => item instanceof File);
      
      if (fileObjects.length > 0) {
        if (onProgress) onProgress(20, 'Uploading gallery images...');
        const galleryImages = await uploadMultipleMedia(fileObjects, (progress, message) => {
          if (onProgress) onProgress(20 + (progress * 0.6), message); // 20-80% for gallery uploads
        });
        galleryIds = galleryImages.map(img => img.id);
      }
    }
    
    // Add existing gallery images from existingImages parameter
    if (existingImages.gallery && existingImages.gallery.length > 0) {
      const existingGalleryIds = existingImages.gallery.map(img => img.id);
      galleryIds = [...existingGalleryIds, ...galleryIds];
    }

    const apiPostData = {
      name_or_title: postData.name_or_title,
      context: postData.context,
      short_description: postData.short_description,
      featured_image: featuredImageId,
      gallery: galleryIds,
      gallery_style: postData.gallery_style || '1-column',
      content_category: postData.content_category,
      car_manufacturer: postData.car_manufacturer,
      car_brand: postData.car_brand,
      vehicle_location: postData.vehicle_location,
      engine_capacity: postData.engine_capacity,
      transmission: postData.transmission,
      drivetrain: postData.drivetrain,
      steering_wheel_position: postData.steering_wheel_position,
      mileage: postData.mileage,
      vehicle_type: postData.vehicle_type,
      condition: postData.condition,
      colour: postData.colour,
      interior_color: postData.interior_color,
      manufacture_year: postData.manufacture_year,
      engine: postData.engine,
      rego: postData.rego,
      sell_status: postData.sell_status,
      comment_status: postData.comment_status || 'closed',
    };
    

    if (onProgress) onProgress(90, 'Updating post...');
    const response = await api.put(`/post-publisher/v1/posts/${postId}`, apiPostData);
    
    if (onProgress) onProgress(100, 'Post updated successfully!');
    return response.data;
  } catch (error) {
    console.error('Error updating post:', error);
    throw new Error(`Failed to update post: ${error.response?.data?.message || error.message}`);
  }
};

// Delete post
export const deletePost = async (postId) => {
  try {
    const response = await api.delete(`/post-publisher/v1/posts/${postId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw new Error(`Failed to delete post: ${error.response?.data?.message || error.message}`);
  }
};

// Get site information
export const getSiteInfo = async () => {
  try {
    const response = await api.get('/post-publisher/v1/site-info');
    return response.data;
  } catch (error) {
    console.error('Error getting site info:', error);
    throw error;
  }
};

export default api;
